import "./utility-tools.css";
import "./qr-code-scanner.css";
import {
  decodedQrSummary,
  qrScanDimensions,
  validateQrImageFile
} from "./qr-code-scanner-core.js";

const root = document.querySelector("[data-qr-scanner]");

if (root) {
  const ids = [
    "qrScannerStatus", "qrScannerWorkspace", "qrScannerInput", "qrScannerDropZone",
    "qrScannerChoose", "qrScannerCamera", "qrScannerCameraPanel", "qrScannerVideo",
    "qrScannerStopCamera", "qrScannerResult", "qrScannerResultKind", "qrScannerValue",
    "qrScannerOpen", "qrScannerCopy", "qrScannerAgain", "qrScannerCanvas"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const pending = new Map();
  const worker = new Worker("/dist/qr-code-scanner-worker.js", { type: "module" });
  let requestId = 0;
  let cameraStream = null;
  let cameraFrame = 0;
  let lastCameraScan = 0;
  let cameraScanPending = false;

  function status(message = "", tone = "error") {
    elements.qrScannerStatus.textContent = message;
    elements.qrScannerStatus.hidden = !message;
    elements.qrScannerStatus.dataset.tone = tone;
  }

  function setBusy(busy, message = "") {
    root.setAttribute("aria-busy", String(busy));
    elements.qrScannerChoose.disabled = busy;
    elements.qrScannerCamera.disabled = busy;
    if (message) status(message, "progress");
  }

  function decodePixels(imageData) {
    const id = ++requestId;
    return new Promise((resolve, reject) => {
      pending.set(id, { resolve, reject });
      worker.postMessage({
        requestId: id,
        pixels: imageData.data.buffer,
        width: imageData.width,
        height: imageData.height
      }, [imageData.data.buffer]);
    });
  }

  worker.addEventListener("message", event => {
    const response = pending.get(event.data?.requestId);
    if (!response) return;
    pending.delete(event.data.requestId);
    if (event.data.error) response.reject(new Error(event.data.error));
    else response.resolve(event.data);
  });

  worker.addEventListener("error", () => {
    for (const response of pending.values())
      response.reject(new Error("The QR scanner stopped unexpectedly."));
    pending.clear();
  });

  function drawToScanCanvas(source, sourceWidth, sourceHeight) {
    const dimensions = qrScanDimensions(sourceWidth, sourceHeight);
    const canvas = elements.qrScannerCanvas;
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return context.getImageData(0, 0, canvas.width, canvas.height);
  }

  async function imageSource(file) {
    if (typeof createImageBitmap === "function") {
      try {
        return await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch {
        return createImageBitmap(file);
      }
    }

    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function releaseImage(source) {
    if (typeof source?.close === "function") source.close();
  }

  function showResult(value) {
    stopCamera();
    const summary = decodedQrSummary(value);
    elements.qrScannerResultKind.textContent = summary.kind;
    elements.qrScannerValue.value = value;
    elements.qrScannerOpen.hidden = !summary.url;
    if (summary.url) elements.qrScannerOpen.href = summary.url;
    else elements.qrScannerOpen.removeAttribute("href");
    elements.qrScannerWorkspace.hidden = true;
    elements.qrScannerCameraPanel.hidden = true;
    elements.qrScannerResult.hidden = false;
    status();
    window.toolboxAnalytics?.track("tool_complete");
    elements.qrScannerValue.focus();
  }

  async function scanFile(file) {
    if (!file) return;
    setBusy(true, "Preparing image…");
    let source;
    try {
      const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
      validateQrImageFile(file, header);
      source = await imageSource(file);
      const width = source.width || source.naturalWidth;
      const height = source.height || source.naturalHeight;
      const imageData = drawToScanCanvas(source, width, height);
      status("Looking for a QR code…", "progress");
      const result = await decodePixels(imageData);
      if (!result.value)
        throw new Error("No QR code was found. Try a sharper image with the complete code visible.");
      showResult(result.value);
    } catch (error) {
      status(error?.message || "The QR code could not be read.");
    } finally {
      releaseImage(source);
      elements.qrScannerInput.value = "";
      setBusy(false);
    }
  }

  function stopCamera() {
    if (cameraFrame) cancelAnimationFrame(cameraFrame);
    cameraFrame = 0;
    cameraScanPending = false;
    if (cameraStream) {
      for (const track of cameraStream.getTracks()) track.stop();
      cameraStream = null;
    }
    elements.qrScannerVideo.srcObject = null;
    elements.qrScannerCameraPanel.hidden = true;
    if (elements.qrScannerResult.hidden) elements.qrScannerWorkspace.hidden = false;
  }

  async function cameraLoop(timestamp) {
    if (!cameraStream) return;
    cameraFrame = requestAnimationFrame(cameraLoop);
    if (cameraScanPending || timestamp - lastCameraScan < 180 || elements.qrScannerVideo.readyState < 2) return;

    cameraScanPending = true;
    lastCameraScan = timestamp;
    try {
      const imageData = drawToScanCanvas(
        elements.qrScannerVideo,
        elements.qrScannerVideo.videoWidth,
        elements.qrScannerVideo.videoHeight
      );
      const result = await decodePixels(imageData);
      if (result.value) showResult(result.value);
    } catch (error) {
      status(error?.message || "The camera frame could not be scanned.");
      stopCamera();
    } finally {
      cameraScanPending = false;
    }
  }

  async function startCamera() {
    status();
    if (!navigator.mediaDevices?.getUserMedia) {
      status("Camera scanning is not available in this browser. Upload a QR image instead.");
      return;
    }

    setBusy(true, "Requesting camera access…");
    window.toolboxAnalytics?.track("tool_start");
    try {
      cameraStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } }
      });
      elements.qrScannerVideo.srcObject = cameraStream;
      elements.qrScannerWorkspace.hidden = true;
      elements.qrScannerCameraPanel.hidden = false;
      await elements.qrScannerVideo.play();
      status("Point the camera at the complete QR code.", "progress");
      cameraFrame = requestAnimationFrame(cameraLoop);
    } catch (error) {
      stopCamera();
      if (error?.name === "NotAllowedError")
        status("Camera access was not allowed. Allow access or upload a QR image instead.");
      else
        status("The camera could not be started. Upload a QR image instead.");
    } finally {
      setBusy(false);
    }
  }

  async function copyValue() {
    try {
      await navigator.clipboard.writeText(elements.qrScannerValue.value);
      status("Copied decoded value.", "success");
    } catch {
      elements.qrScannerValue.select();
      status("Select and copy the decoded value.", "progress");
    }
  }

  function reset() {
    stopCamera();
    elements.qrScannerResult.hidden = true;
    elements.qrScannerWorkspace.hidden = false;
    elements.qrScannerValue.value = "";
    status();
    elements.qrScannerChoose.focus();
  }

  elements.qrScannerChoose.addEventListener("click", () => elements.qrScannerInput.click());
  elements.qrScannerInput.addEventListener("change", () => scanFile(elements.qrScannerInput.files?.[0]));
  elements.qrScannerCamera.addEventListener("click", startCamera);
  elements.qrScannerStopCamera.addEventListener("click", stopCamera);
  elements.qrScannerCopy.addEventListener("click", copyValue);
  elements.qrScannerAgain.addEventListener("click", reset);
  elements.qrScannerDropZone.addEventListener("click", event => {
    if (event.target === elements.qrScannerDropZone) elements.qrScannerInput.click();
  });
  elements.qrScannerDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      elements.qrScannerInput.click();
    }
  });
  for (const type of ["dragenter", "dragover"]) {
    elements.qrScannerDropZone.addEventListener(type, event => {
      event.preventDefault();
      elements.qrScannerDropZone.classList.add("is-dragging");
    });
  }
  for (const type of ["dragleave", "drop"]) {
    elements.qrScannerDropZone.addEventListener(type, event => {
      event.preventDefault();
      elements.qrScannerDropZone.classList.remove("is-dragging");
    });
  }
  elements.qrScannerDropZone.addEventListener("drop", event => scanFile(event.dataTransfer?.files?.[0]));

  window.addEventListener("pagehide", () => {
    stopCamera();
    worker.terminate();
  }, { once: true });
}
